import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useDeleteSupplierMutation,
  useGetSupplierQuery,
} from "@/redux/services/inventory/supplier.api";
import { useGetPurchaseOrdersQuery } from "@/redux/services/inventory/purchaseOrder.api";
import { Supplier } from "@/redux/services/inventory/inventory.type";
import { displayPrice, formatPhoneNumber } from "@/lib/utils";
import { getPaymentTermsLabel } from "@/lib/paymentTerms";
import { useRestaurantCurrency } from "@/lib/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import { canRecordPurchaseOrders } from "@/lib/permissions";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { Pencil, Trash2, Phone, Mail, ShoppingBag, Plus } from "lucide-react";
import { useConfirm } from "@/components/ui/confirm-dialog";
import toast from "react-hot-toast";
import SupplierFormModal from "./SupplierFormModal";
import PurchaseOrderStatusBadge from "@/components/inventory/purchase-orders/PurchaseOrderStatusBadge";

interface SupplierDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  supplierId: string | null;
}

const SupplierDetailsDrawer = ({
  isOpen,
  onClose,
  restaurantId,
  supplierId,
}: SupplierDetailsDrawerProps) => {
  const currency = useRestaurantCurrency();
  const { can, canManage } = usePermissions();
  const canManageSuppliers = canManage("suppliers");
  // "Nouveau Bon" opens the purchase form (SFRESTO-165).
  const canRecordPurchases = canRecordPurchaseOrders(can);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: supplier, isLoading } = useGetSupplierQuery(
    { supplierUuid: supplierId ?? "" },
    { skip: !supplierId }
  );

  const { data: poData, isFetching: isFetchingPOs } = useGetPurchaseOrdersQuery(
    {
      restaurantId,
      query: { supplierUuid: supplierId ?? undefined, limit: 10 },
    },
    { skip: !supplierId }
  );

  const [deleteSupplier] = useDeleteSupplierMutation();
  const confirm = useConfirm();

  const handleDelete = async (s: Supplier) => {
    const ok = await confirm({
      title: `Supprimer "${s.name}" ?`,
      description: "Cette action est irréversible.",
      confirmText: "Supprimer",
      cancelText: "Annuler",
      variant: "destructive",
      icon: <Trash2 className="h-6 w-6" />,
    });
    if (!ok) return;
    const res = await deleteSupplier({ supplierUuid: s.id });
    if ("error" in res) {
      toast.error("Erreur lors de la suppression");
      return;
    }
    toast.success("Fournisseur supprimé avec succès");
    onClose();
  };

  const orders = poData?.results ?? [];

  return (
    <>
      <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 overflow-y-auto"
        >
          <div className="p-6 space-y-6">
            {isLoading || !supplier ? (
              <div className="space-y-4">
                <Skeleton className="w-3/4 h-6" />
                <Skeleton className="w-full h-24" />
                <Skeleton className="w-full h-40" />
              </div>
            ) : (
              <>
                <SheetHeader>
                  <SheetTitle>{supplier.name}</SheetTitle>
                  {supplier.contactName && (
                    <SheetDescription>
                      Contact : {supplier.contactName}
                    </SheetDescription>
                  )}
                </SheetHeader>

                {(supplier.phone || supplier.email) && (
                  <div className="dash__card p-3 space-y-2">
                    {supplier.phone && (
                      <a
                        href={`tel:${supplier.phone}`}
                        className="flex items-center gap-2 text-sm text-card-foreground hover:text-blue-700"
                      >
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {formatPhoneNumber(supplier.phone)}
                      </a>
                    )}
                    {supplier.email && (
                      <a
                        href={`mailto:${supplier.email}`}
                        className="flex items-center gap-2 text-sm text-card-foreground hover:text-blue-700"
                      >
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {supplier.email}
                      </a>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">
                      Conditions de paiement
                    </p>
                    <p className="text-sm font-medium text-card-foreground mt-1">
                      {getPaymentTermsLabel(supplier.paymentTerms)}
                    </p>
                  </div>
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground">
                      Délai de livraison
                    </p>
                    <p className="text-sm font-medium text-card-foreground mt-1">
                      {supplier.leadTimeDays !== null &&
                      supplier.leadTimeDays !== undefined
                        ? `${supplier.leadTimeDays} j`
                        : "—"}
                    </p>
                  </div>
                </div>

                {supplier.notes && (
                  <div className="dash__card p-3">
                    <p className="text-xs text-muted-foreground mb-1">Notes</p>
                    <p className="text-sm whitespace-pre-wrap">
                      {supplier.notes}
                    </p>
                  </div>
                )}

                <div className="flex flex-wrap gap-2">
                  {canRecordPurchases && (
                    <Link
                      to={`/inventory/purchase-orders/new?supplier=${supplier.id}`}
                      onClick={onClose}
                    >
                      <Button type="button" size="sm">
                        <Plus className="h-4 w-4" />
                        Nouveau Bon
                      </Button>
                    </Link>
                  )}
                  {canManageSuppliers && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => setIsEditOpen(true)}
                    >
                      <Pencil className="h-4 w-4" />
                      Modifier
                    </Button>
                  )}
                  {canManageSuppliers && (
                    <Button
                      type="button"
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(supplier)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Supprimer
                    </Button>
                  )}
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-card-foreground flex items-center gap-2">
                      <ShoppingBag className="h-4 w-4 text-muted-foreground" />
                      Bons de commande
                    </h3>
                    {orders.length > 0 && (
                      <Link
                        to={`/inventory/purchase-orders?supplier=${supplier.id}`}
                        className="text-xs text-blue-600 hover:text-blue-700 hover:underline"
                        onClick={onClose}
                      >
                        Voir tout
                      </Link>
                    )}
                  </div>
                  {isFetchingPOs ? (
                    <div className="space-y-2">
                      {Array.from({ length: 3 }).map((_, i) => (
                        <Skeleton key={i} className="w-full h-12" />
                      ))}
                    </div>
                  ) : orders.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      Aucun bon de commande pour ce fournisseur
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {orders.map((po) => (
                        <li key={po.id}>
                          <Link
                            to={`/inventory/purchase-orders/${po.id}`}
                            onClick={onClose}
                            className="block bg-muted/40 rounded-md px-3 py-2 hover:bg-muted transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2 flex-wrap">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-card-foreground">
                                  {po.poNumber}
                                </span>
                                <PurchaseOrderStatusBadge status={po.status} />
                              </div>
                              <span className="text-sm font-medium">
                                {displayPrice(po.totalAmount, currency)}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              {format(parseISO(po.orderDate), "d MMM yyyy", {
                                locale: fr,
                              })}
                            </p>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {supplier && isEditOpen && (
        <SupplierFormModal
          isOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
          supplierToEdit={supplier}
          restaurantId={restaurantId}
        />
      )}
    </>
  );
};

export default SupplierDetailsDrawer;
